import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { BaseColorControl } from "@/components/settings/base-color-control";
import { ComponentsPage } from "@/components/settings/components-catalog";
import { MotionPrefControl } from "@/components/settings/motion-pref-control";
import { SecondaryColorControl } from "@/components/settings/secondary-color-control";
import { currentRole } from "@/lib/pb";
import { useDocumentTitle } from "@/lib/use-document-title";
import { cn } from "@/lib/utils";

type TabId = "colour" | "motion" | "components";

interface TabDef {
  id: TabId;
  label: string;
}

export const Route = createFileRoute("/_app/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  useDocumentTitle("Settings");
  const isSupport = currentRole() === "support";

  const tabs: TabDef[] = [
    { id: "colour", label: "Colour" },
    { id: "motion", label: "Motion" },
    ...(isSupport ? [{ id: "components" as const, label: "Components" }] : []),
  ];

  const [active, setActive] = useState<TabId>("colour");

  // Components tab is wider — uses its own internal scaffolding. Other tabs
  // are narrow column reading width.
  const isWide = active === "components";

  return (
    <div className={cn("flex flex-col gap-6 p-6 md:p-8", isWide ? "" : "mx-auto max-w-3xl")}>
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Theme and motion preferences. Saved to this browser; resets on incognito.
        </p>
      </header>

      <nav
        role="tablist"
        aria-label="Settings sections"
        className="inline-flex w-fit gap-1 rounded-lg border border-border bg-muted/40 p-1"
      >
        {tabs.map((tab) => {
          const selected = active === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`settings-panel-${tab.id}`}
              onClick={() => setActive(tab.id)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                selected
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>

      {active === "colour" && (
        <section
          role="tabpanel"
          id="settings-panel-colour"
          aria-labelledby="settings-tab-colour"
          className="space-y-8"
        >
          <BaseColorControl />
          <SecondaryColorControl />
        </section>
      )}

      {active === "motion" && (
        <section
          role="tabpanel"
          id="settings-panel-motion"
          aria-labelledby="settings-tab-motion"
          className="space-y-4"
        >
          <MotionPrefControl />
        </section>
      )}

      {active === "components" && isSupport && (
        <section
          role="tabpanel"
          id="settings-panel-components"
          aria-labelledby="settings-tab-components"
        >
          <ComponentsPage />
        </section>
      )}
    </div>
  );
}
